import React, { useRef, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import ModalDialog, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Inline, Stack, Text } from '@atlaskit/primitives';

export type GroupNotifyWarningDialogProps = {
	groupNames: string[];
	isSaving: boolean;
	onDoNotify: () => void;
	onDoNotNotify: () => void;
	onCloseComplete: () => void;
};

const i18n = defineMessages({
	heading: {
		id: 'share-and-restrict-dialog.group-notify-warning.heading',
		defaultMessage: 'Notify everyone in the group?',
		description:
			'Heading for a pop-up modal that asks a user to confirm sending notifications to all group members',
	},
	body: {
		id: 'share-and-restrict-dialog.group-notify-warning.body',
		defaultMessage:
			'Groups often contain a lot of people. It might be a little noisy to notify them all. Are you sure you want to notify everyone in these groups?',
		description: 'Body for a pop-up modal that interrupts sharing content to groups',
	},
	doNotifyButton: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-notify',
		defaultMessage: 'Notify groups',
		description:
			'The label for a button that will share a document and notify all new users, including those in groups',
	},
	doNotNotifyButton: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-not-notify',
		// TODO: This might change, but right now it's considered final:
		// https://atlassian.slack.com/archives/C06MVDCR1FZ/p1725543038992469?thread_ts=1725477040.948949&cid=C06MVDCR1FZ
		defaultMessage: 'Share without notifying',
		description:
			'The label for a button that will share a document, and notify individuals but not group members',
	},
});

export const GroupNotifyWarningDialog: FC<GroupNotifyWarningDialogProps> = ({
	groupNames,
	isSaving,
	onDoNotify,
	onDoNotNotify,
	onCloseComplete,
}) => {
	const focusRef = useRef<HTMLButtonElement>(null);

	return (
		<ModalDialog width="medium" autoFocus={focusRef} onCloseComplete={onCloseComplete}>
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage {...i18n.heading} />
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Stack space="space.200">
					<FormattedMessage {...i18n.body} />
					<Inline>
						<Text weight="bold">{groupNames.join(', ')}</Text>
					</Inline>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button isDisabled={isSaving} onClick={onDoNotNotify}>
					<FormattedMessage {...i18n.doNotNotifyButton} />
				</Button>
				<Button appearance="primary" isDisabled={isSaving} onClick={onDoNotify} ref={focusRef}>
					<FormattedMessage {...i18n.doNotifyButton} />
				</Button>
			</ModalFooter>
		</ModalDialog>
	);
};
